import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Package, Clock, Gift } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Orders = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchOrders();
  }, [user]);

  const fetchOrders = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/orders`, {
        withCredentials: true,
      });
      setOrders(data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-[#C27054] text-white';
      case 'processing':
        return 'bg-[#E4E8DF] text-[#2D3B26]';
      case 'shipped':
        return 'bg-[#7A8A73] text-white';
      case 'delivered':
        return 'bg-[#2D3B26] text-white';
      default:
        return 'bg-[#E4E8DF] text-[#2D3B26]';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C27054] mx-auto mb-4"></div>
          <p className="text-[#7A8A73]">Loading orders...</p>
        </div>
      </div>
    );
  }

  const isEmpty = orders.length === 0;

  return (
    <div className="min-h-screen bg-[#FDFBF7] pt-24 md:pt-32 pb-16 md:pb-24 px-4 md:px-6" data-testid="orders-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 md:mb-12">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#7A8A73] mb-2">Your Account</p>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif tracking-tight text-[#2D3B26] leading-[1.1]">
            Order History
          </h1>
        </div>

        {isEmpty ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <Package size={64} className="mx-auto text-[#7A8A73] mb-4" />
            <p className="text-[#7A8A73] text-lg mb-6">No orders yet</p>
            <a
              href="/products"
              data-testid="start-shopping-button"
              className="inline-block bg-[#C27054] text-white rounded-full px-8 py-4 hover:bg-[#A85B42] transition-all duration-300 shadow-[0_4px_14px_0_rgba(194,112,84,0.39)] hover:-translate-y-0.5 text-sm uppercase tracking-widest font-semibold"
            >
              Start Shopping
            </a>
          </motion.div>
        ) : (
          <div className="space-y-6">
            {orders.map((order, index) => (
              <motion.div
                key={order.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6"
                data-testid={`order-card-${order.id}`}
              >
                <div className="flex flex-wrap justify-between items-start mb-6 gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-[#7A8A73] mb-1">Order ID</p>
                    <p className="text-sm font-mono text-[#2D3B26]" data-testid={`order-id-${order.id}`}>
                      {order.id.slice(0, 8).toUpperCase()}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <Clock size={16} className="text-[#7A8A73]" />
                    <span className="text-sm text-[#475740]">{formatDate(order.created_at)}</span>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    {order.gift_wrap && (
                      <span
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-semibold bg-[#C27054]/10 text-[#C27054] border border-[#C27054]/30"
                        data-testid={`order-gift-badge-${order.id}`}
                      >
                        <Gift size={12} /> Gift Wrapped
                      </span>
                    )}
                    <span
                      className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest font-semibold ${
                        getStatusColor(order.status)
                      }`}
                      data-testid={`order-status-${order.id}`}
                    >
                      {order.status}
                    </span>
                  </div>
                </div>

                {/* Order Items */}
                <div className="space-y-3 mb-6">
                  {order.items?.map((item, idx) => (
                    <div key={idx} className="flex gap-4 items-center">
                      <div className="flex-1">
                        <p className="text-sm font-serif text-[#2D3B26]">{item.product_name}</p>
                        <p className="text-xs text-[#7A8A73]">Quantity: {item.quantity}</p>
                      </div>
                      <p className="text-sm font-medium text-[#475740]">₹{item.subtotal}</p>
                    </div>
                  ))}
                </div>

                {/* Order Details */}
                <div className="border-t border-[rgba(45,59,38,0.1)] pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-[#7A8A73] mb-1">Ship To</p>
                      {(order.first_name || order.last_name) && (
                        <p className="text-sm font-semibold text-[#2D3B26]">
                          {order.first_name} {order.last_name}
                        </p>
                      )}
                      <p className="text-sm text-[#475740]">{order.shipping_address}</p>
                      {(order.city || order.state || order.pincode) && (
                        <p className="text-sm text-[#475740]">
                          {[order.city, order.state, order.pincode].filter(Boolean).join(', ')}
                        </p>
                      )}
                      {order.expected_delivery_date && (
                        <p className="text-xs text-[#C27054] mt-1 flex items-center gap-1" data-testid={`order-delivery-${order.id}`}>
                          <Clock size={11} /> Expected by {new Date(order.expected_delivery_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-xs uppercase tracking-widest text-[#7A8A73] mb-1">Total Amount</p>
                      <p className="text-2xl font-serif text-[#C27054]" data-testid={`order-total-${order.id}`}>
                        ₹{Number(order.total_amount).toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                  {order.gift_wrap && order.gift_message && (
                    <div className="mt-4 bg-[#fff5f0] border border-[#C27054]/20 rounded-xl p-4" data-testid={`order-gift-message-${order.id}`}>
                      <p className="text-xs uppercase tracking-widest font-semibold text-[#C27054] mb-1 flex items-center gap-1">
                        <Gift size={12} /> Gift Message
                      </p>
                      <p className="text-sm text-[#475740] italic">"{order.gift_message}"</p>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;